import React, { useState } from "react";
import api from "@/lib/api";
import { PageHeader, TableShell } from "@/components/ui-kit";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function weekAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toISOString().slice(0, 10);
}

// Saves an authenticated blob response with the filename the server picked, the same pattern
// used on the Claims page for any file behind the auth header.
function saveBlob(res, fallbackName) {
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackName;
  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
}

export default function DriverPay() {
  const [weekStart, setWeekStart] = useState(weekAgoIso());
  const [weekEnd, setWeekEnd] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null); // { row_count, drivers: [{driver_name, ride_count, total_earnings}] }

  const preview = () => {
    setLoading(true);
    setResult(null);
    api
      .get("/driverpay/export", { params: { week_start: weekStart, week_end: weekEnd } })
      .then((r) => setResult(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not build the preview"))
      .finally(() => setLoading(false));
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/driverpay/export", {
        params: { week_start: weekStart, week_end: weekEnd, file: "csv" },
        responseType: "blob",
      });
      saveBlob(res, `driverpay_${weekStart}_to_${weekEnd}.csv`);
    } catch (e) {
      toast.error("Could not generate the file. Run Preview again to see what changed.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="driverpay-page">
      <PageHeader subtitle="Reporting" title="Driver Pay Export" />

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Week start</label>
            <input
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm"
              data-testid="driverpay-week-start"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Week end</label>
            <input
              type="date"
              value={weekEnd}
              onChange={(e) => setWeekEnd(e.target.value)}
              className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm"
              data-testid="driverpay-week-end"
            />
          </div>
          <button onClick={preview} disabled={loading} className="btn-primary disabled:opacity-60 flex items-center gap-1.5" data-testid="driverpay-preview-btn">
            <Search size={14} /> {loading ? "Checking..." : "Preview"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Builds a CSV, in HichPay's own rides-table shape, from this agency's reconciled completed rides in the
          range above. Each ride is paid the greater of the MN minimum-wage floor or its real reconciled fare.
          The file is a handoff for a manual load into HichPay, it does not write to HichPay itself.
        </p>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="driverpay-result">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-700">
              {result.row_count} ride{result.row_count === 1 ? "" : "s"} across {result.drivers.length} driver{result.drivers.length === 1 ? "" : "s"}
            </div>
            <button
              onClick={download}
              disabled={result.row_count === 0 || downloading}
              className="btn-primary disabled:opacity-60 flex items-center gap-1.5"
              data-testid="driverpay-download-btn"
            >
              <Download size={14} /> {downloading ? "Preparing..." : "Download CSV"}
            </button>
          </div>
          {result.drivers.length > 0 && (
            <TableShell columns={["Driver", "Rides", "Total Earnings"]}>
              {result.drivers.map((d) => (
                <tr key={d.driver_name} className="tbl-row border-t border-slate-100">
                  <td className="px-4 py-2.5 text-sm">{d.driver_name}</td>
                  <td className="px-4 py-2.5 text-sm">{d.ride_count}</td>
                  <td className="px-4 py-2.5 text-sm">${d.total_earnings.toFixed(2)}</td>
                </tr>
              ))}
            </TableShell>
          )}
        </div>
      )}
    </div>
  );
}
